/**
 * Error type categories (machine-readable)
 * Matches backend ErrorType enum
 */
export enum ErrorType {
  VALIDATION_ERROR = "validation_error",
  AUTHENTICATION_ERROR = "authentication_error",
  AUTHORIZATION_ERROR = "authorization_error",
  NOT_FOUND = "not_found",
  RATE_LIMIT_ERROR = "rate_limit_error",
  CONFLICT = "conflict",
  INTERNAL_ERROR = "internal_error"
}

/**
 * Specific error codes (machine-readable)
 * Matches backend ErrorCode enum
 */
export enum ErrorCode {
  // Validation errors
  INVALID_EMAIL = "invalid_email",
  INVALID_FORMAT = "invalid_format",
  MISSING_REQUIRED_FIELD = "missing_required_field",
  VALUE_TOO_LONG = "value_too_long",
  VALUE_TOO_SHORT = "value_too_short",
  INVALID_VALUE = "invalid_value",
  USERS_NOT_IN_ORGANIZATION = "users_not_in_organization",

  // Authentication errors
  TOKEN_EXPIRED = "token_expired",
  TOKEN_INVALID = "token_invalid",
  MISSING_CREDENTIALS = "missing_credentials",
  INVALID_CREDENTIALS = "invalid_credentials",

  // Authorization errors
  INSUFFICIENT_PERMISSIONS = "insufficient_permissions",
  FORBIDDEN_ACTION = "forbidden_action",

  // Not found errors
  RESOURCE_NOT_FOUND = "resource_not_found",

  // Rate limit errors
  RATE_LIMIT_EXCEEDED = "rate_limit_exceeded",
  /**
   * The rate-limit message for when the delay is UNKNOWN, i.e. no `retry-after`
   * header was sent. It exists because `rate_limit_exceeded` carries a
   * `{retryAfter}` placeholder and {@link interpolateParams} returns the message
   * untouched when no params are supplied (`if (!params) return message`) — so
   * rendering that entry without the header ships the literal `{retryAfter}` to a
   * user. A `retry-after` header is not guaranteed to be present.
   *
   * This member is FRONTEND-ONLY and is deliberately absent from the backend's
   * `ErrorCode` in `_common/dto/error-response.dto.ts`: it names a rendering
   * choice the client makes from the response HEADERS, not a condition a server
   * reports in a body. Nothing sends it over the wire.
   */
  RATE_LIMIT_EXCEEDED_UNKNOWN_DELAY = "rate_limit_exceeded_unknown_delay",

  // Conflict errors
  RESOURCE_ALREADY_EXISTS = "resource_already_exists",
  DUPLICATE_ENTRY = "duplicate_entry",

  // Internal errors
  INTERNAL_SERVER_ERROR = "internal_server_error",
  SERVICE_UNAVAILABLE = "service_unavailable"
}

/**
 * Translation parameters for error messages
 * Used for interpolating dynamic values into translated messages
 */
export interface ErrorTranslationParams {
  [key: string]: string | number
}

/**
 * Structure of error translation files
 */
export interface ErrorTranslations {
  errors: {
    [key in ErrorType]: {
      [code: string]: string
    }
  }
}
